import React from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { colors, spacing, typography, borderRadius } from '../styles/theme';

interface ProtectedRouteProps {
  children: React.ReactElement;
  requireRole?: 'contributor' | 'administrator';
}

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: ${colors.background.primary};
`;

const LoadingSpinner = styled.div`
  width: 48px;
  height: 48px;
  border: 4px solid ${colors.border.light};
  border-top: 4px solid ${colors.primary[500]};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  margin-top: ${spacing[4]};
  color: ${colors.text.secondary};
  font-size: ${typography.fontSizes.base};
`;

const UnauthorizedContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: ${colors.background.primary};
  padding: ${spacing[6]};
`;

const UnauthorizedCard = styled.div`
  background: ${colors.white};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[8]};
  max-width: 500px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

const UnauthorizedTitle = styled.h1`
  font-size: ${typography.fontSizes['2xl']};
  font-weight: ${typography.fontWeights.bold};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[4]};
`;

const UnauthorizedMessage = styled.p`
  font-size: ${typography.fontSizes.base};
  color: ${colors.text.secondary};
  margin-bottom: ${spacing[6]};
  line-height: ${typography.lineHeights.relaxed};
`;

const LoginButton = styled.button`
  background: ${colors.primary[500]};
  color: ${colors.white};
  border: none;
  border-radius: ${borderRadius.lg};
  padding: ${spacing[3]} ${spacing[6]};
  font-size: ${typography.fontSizes.base};
  font-weight: ${typography.fontWeights.semibold};
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${colors.primary[600]};
    transform: translateY(-1px);
  }
  
  &:focus {
    outline: 2px solid ${colors.primary[500]};
    outline-offset: 2px;
  }
`;

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  children, 
  requireRole 
}) => {
  const { userInfo, isLoading } = useAuth();
  const location = useLocation();

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        <LoadingText>Checking authentication...</LoadingText>
      </LoadingContainer>
    );
  }

  // Not authenticated - redirect to login
  if (!userInfo.isAuthenticated) {
    return (
      <UnauthorizedContainer>
        <UnauthorizedCard>
          <UnauthorizedTitle>Authentication Required</UnauthorizedTitle>
          <UnauthorizedMessage>
            You need to sign in to access the admin area. Please log in with your Azure AD account.
          </UnauthorizedMessage>
          <LoginButton onClick={() => {
            window.location.href = `/.auth/login/github?post_login_redirect_uri=${encodeURIComponent(location.pathname)}`;
          }}>
            Sign In with Azure AD
          </LoginButton>
        </UnauthorizedCard>
      </UnauthorizedContainer>
    );
  }

  // Check role if required
  if (requireRole) {
    const hasRequiredRole = requireRole === 'administrator' 
      ? userInfo.isAdmin 
      : userInfo.isContributor;

    if (!hasRequiredRole) {
      return (
        <UnauthorizedContainer>
          <UnauthorizedCard>
            <UnauthorizedTitle>Access Denied</UnauthorizedTitle>
            <UnauthorizedMessage>
              You don't have the required permissions to access this area. 
              The <strong>{requireRole}</strong> role is required.
            </UnauthorizedMessage>
            <LoginButton onClick={() => window.location.href = '/'}>
              Return to Home
            </LoginButton>
          </UnauthorizedCard>
        </UnauthorizedContainer>
      );
    }
  }

  // Authenticated and authorized
  return children;
};
